import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ChangeDetectionStrategy,
    ElementRef,
    computed,
    effect,
    inject,
    input,
    viewChild,
} from '@angular/core';
import { ClusterService, OidcService, TokenBrokerService } from '@fry-it/risr-core';
import {
    defineRisrAiAssistant,
    type RisrAiAssistantConfig,
    type RisrAiAssistantElement,
    type RisrAiContext,
} from '@fry-it/risr-ai';

/** RFC 8707 resource indicator for the `risrai` audience. */
const RISRAI_RESOURCE = 'https://risr.global/resources/risrai';

/**
 * Smart container that mounts the framework-agnostic `<risr-ai-assistant>`
 * Web Component (from `@fry-it/risr-ai`) into a Kaizen/Assembly app shell.
 *
 * Wiring is deploy-driven: the widget only renders when the current
 * organisation has a `risrai` backend URL configured (populated from
 * `conf/zorg.conf` at cluster setup time). Auth token and organisation are
 * sourced from `OidcService`; the raw access token is passed through and the
 * widget adds its own `Bearer` prefix.
 *
 * Page context (e.g. the trainee whose e-portfolio is being reviewed) is
 * optional and supplied by the host app via the `context` input.
 */
@Component({
    selector: 'risr-ai-assistant-host',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    template: `
        @if (apiBaseUrl() && organisation()) {
            <risr-ai-assistant #el></risr-ai-assistant>
        }
    `,
})
export class RisrAiAssistantHostComponent {
    private readonly cluster = inject(ClusterService);
    private readonly oidc = inject(OidcService);
    private readonly tokenBroker = inject(TokenBrokerService);

    /** Optional page context injected into the assistant prompt (untrusted). */
    readonly context = input<RisrAiContext | undefined>(undefined);

    private readonly el =
        viewChild<ElementRef<RisrAiAssistantElement>>('el');

    protected readonly apiBaseUrl = computed(() => {
        try {
            return this.cluster.getCurrentUrls().risrai ?? '';
        } catch {
            return '';
        }
    });

    protected readonly organisation = computed(
        () => this.oidc.$currentOrganisation() ?? '',
    );

    private readonly getToken: RisrAiAssistantConfig['getToken'] = async () => {
        const organisation = this.organisation();
        if (!organisation) {
            return '';
        }
        // Option (a): the active organisation is passed as a dynamic
        // `org:<id>` scope so the IdP can validate membership and stamp a
        // trusted `organisation` claim on the exchanged `risrai` token
        // (the `EAS-Organisation` header is untrusted). If the org-binding
        // encoding changes, this is the only place that needs updating.
        const scope = [
            `org:${organisation}`,
            'risrai:usage:chat',
            'risrai:usage:stats',
            'risrai:setup',
        ].join(' ');
        return this.tokenBroker.getToken(RISRAI_RESOURCE, scope);
    };

    constructor() {
        defineRisrAiAssistant();

        effect(() => {
            const host = this.el()?.nativeElement;
            const apiBaseUrl = this.apiBaseUrl();
            const organisation = this.organisation();
            if (!host || !apiBaseUrl || !organisation) {
                return;
            }
            host.config = { apiBaseUrl, organisation, getToken: this.getToken };
        });

        effect(() => {
            const host = this.el()?.nativeElement;
            if (host) {
                host.context = this.context();
            }
        });
    }
}
